import type { EditorSnapshot } from "../editor/editor_snapshot";
import type { PreviewProducer } from "./board_session";

export type SaveStatus =
  | { readonly kind: "idle" }
  | { readonly kind: "saving" }
  | { readonly kind: "saved"; readonly savedAt: Date }
  | { readonly kind: "failed"; readonly message: string };

export interface AutosaveOperations {
  stageRecovery(snapshot: EditorSnapshot): Promise<void>;
  save(snapshot: EditorSnapshot, previewProducer?: PreviewProducer): Promise<void>;
}

interface PendingSave {
  readonly snapshot: EditorSnapshot;
  readonly previewProducer?: PreviewProducer;
  readonly recovery: Promise<void>;
}

/** Coalesces editor mutations while retaining flush and retry semantics. */
export class AutosaveController {
  private readonly operations: AutosaveOperations;
  private readonly onStatus: (status: SaveStatus) => void;
  private readonly onSaved: () => void;
  private readonly debounceMilliseconds: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: PendingSave | null = null;
  private activeSave: Promise<boolean> | null = null;

  /** Creates one controller per active board session. */
  public constructor(
    operations: AutosaveOperations,
    onStatus: (status: SaveStatus) => void,
    onSaved: () => void,
    debounceMilliseconds = 500,
  ) {
    this.operations = operations;
    this.onStatus = onStatus;
    this.onSaved = onSaved;
    this.debounceMilliseconds = debounceMilliseconds;
  }

  /** Stages recovery immediately and restarts the canonical-save debounce. */
  public schedule(snapshot: EditorSnapshot, previewProducer?: PreviewProducer): void {
    // Attach rejection handling immediately so delayed canonical saves cannot leak errors.
    const recovery = this.operations.stageRecovery(snapshot).catch(() => undefined);
    this.pending =
      previewProducer === undefined
        ? { snapshot, recovery }
        : { snapshot, previewProducer, recovery };
    this.onStatus({ kind: "saving" });
    if (this.timer !== null) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.debounceMilliseconds);
  }

  /** Flushes the newest candidate and then any mutation received during its write. */
  public async flush(): Promise<boolean> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.activeSave !== null) {
      const saved = await this.activeSave;
      if (saved && this.pending !== null) {
        return this.flush();
      }
      return saved;
    }
    if (this.pending === null) {
      return true;
    }

    const candidate = this.pending;
    this.pending = null;
    this.activeSave = this.persist(candidate);
    const saved = await this.activeSave;
    this.activeSave = null;

    if (saved && this.pending !== null) {
      return this.flush();
    }
    return saved;
  }

  /** Retries the retained candidate after a failed canonical write. */
  public async retry(): Promise<boolean> {
    this.onStatus({ kind: "saving" });
    return this.flush();
  }

  /** Drops only the retained in-memory candidate after an explicit recovery decision. */
  public discardPending(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.pending = null;
  }

  /** Cancels only the timer while leaving recovery and canonical data untouched. */
  public dispose(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Runs the ordered recovery and canonical operations for one candidate. */
  private async persist(candidate: PendingSave): Promise<boolean> {
    try {
      await candidate.recovery;
      await this.operations.save(candidate.snapshot, candidate.previewProducer);
      this.onStatus({ kind: "saved", savedAt: new Date() });
      this.onSaved();
      return true;
    } catch {
      this.pending ??= candidate;
      this.onStatus({
        kind: "failed",
        message:
          "Save failed. The disk copy was not overwritten. Keep this tab open, then retry or save as a copy.",
      });
      return false;
    }
  }
}
